/**
 * ProfileModal - User profile view/edit modal with memory slideshow
 * Desktop: centered modal, Mobile: bottom sheet
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { getUserProfile, updateUserProfile, checkHandleAvailable, getUserMemories, deleteMemoryV2 } from '../../utils/supabase';
import { COUNTRIES } from '../../data/countries';
import { ZODIAC_SIGNS } from '../../data/zodiacSigns';
import { LANGUAGES } from '../../data/languages';
import { logger } from '../../utils/logger';
import './ProfileModal.css';

const MAX_BIO = 300;
const MAX_LANGUAGES = 5;
const SLIDESHOW_INTERVAL = 4000;
const NOTES_PER_PAGE_DESKTOP = 4;
const NOTES_PER_PAGE_MOBILE = 3;

export default function ProfileModal({ isOpen, onClose, user }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [memories, setMemories] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [notesPerPage, setNotesPerPage] = useState(NOTES_PER_PAGE_DESKTOP);
  const [memoryToDelete, setMemoryToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Form state
  const [handle, setHandle] = useState('');
  const [bio, setBio] = useState('');
  const [country, setCountry] = useState('');
  const [zodiacSign, setZodiacSign] = useState('');
  const [languages, setLanguages] = useState([]);

  // Handle validation
  const [handleStatus, setHandleStatus] = useState({ checking: false, available: true, valid: true, error: null });
  const handleCheckTimeout = useRef(null);

  // Country search
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);

  // Languages dropdown
  const [showLanguagesDropdown, setShowLanguagesDropdown] = useState(false);
  const [languageSearch, setLanguageSearch] = useState('');

  const modalRef = useRef(null);
  const slideshowRef = useRef(null);

  const loadProfile = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const result = await getUserProfile(user);
    if (result.success && result.profile) {
      setProfile(result.profile);
      setHandle(result.profile.handle || '');
      setBio(result.profile.bio || '');
      setCountry(result.profile.country || '');
      setZodiacSign(result.profile.zodiac_sign || '');
      setLanguages(result.profile.languages || []);
    }
    setLoading(false);
  }, [user]);

  const loadMemories = useCallback(async () => {
    if (!user) return;
    try {
      const userMemories = await getUserMemories(user);
      setMemories(userMemories || []);
    } catch (err) {
      logger.error('Failed to load memories:', err);
    }
  }, [user]);

  // Load profile and memories on open
  useEffect(() => {
    if (isOpen && user) {
      loadProfile();
      loadMemories();
    }
  }, [isOpen, user, loadProfile, loadMemories]);

  // Detect mobile for responsive notes per page
  useEffect(() => {
    const handleResize = () => {
      setNotesPerPage(window.innerWidth <= 640 ? NOTES_PER_PAGE_MOBILE : NOTES_PER_PAGE_DESKTOP);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate total pages
  const totalPages = Math.ceil(memories.length / notesPerPage);

  // Auto-advance slideshow
  useEffect(() => {
    if (totalPages <= 1) return;

    slideshowRef.current = setInterval(() => {
      setCurrentPage((prev) => (prev + 1) % totalPages);
    }, SLIDESHOW_INTERVAL);

    return () => clearInterval(slideshowRef.current);
  }, [totalPages]);

  // Get current page of memories
  const getCurrentPageMemories = () => {
    const start = currentPage * notesPerPage;
    return memories.slice(start, start + notesPerPage);
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Debounced handle check
  const checkHandle = useCallback(async (value) => {
    if (value === profile?.handle) {
      setHandleStatus({ checking: false, available: true, valid: true, error: null });
      return;
    }

    setHandleStatus({ checking: true, available: false, valid: true, error: null });

    const result = await checkHandleAvailable(value, user.uid);
    setHandleStatus({
      checking: false,
      available: result.available,
      valid: result.valid,
      error: result.error,
    });
  }, [profile?.handle, user?.uid]);

  const handleHandleChange = (e) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
    setHandle(value);

    // Clear previous timeout
    if (handleCheckTimeout.current) {
      clearTimeout(handleCheckTimeout.current);
    }

    // Debounce the check
    if (value.length >= 3) {
      handleCheckTimeout.current = setTimeout(() => {
        checkHandle(value);
      }, 500);
    } else {
      setHandleStatus({ checking: false, available: false, valid: false, error: 'Handle must be at least 3 characters' });
    }
  };

  const handleSave = async () => {
    if (!handleStatus.available || !handleStatus.valid) return;

    setSaving(true);
    const result = await updateUserProfile(user, {
      handle: handle !== profile?.handle ? handle : null,
      bio: bio || null,
      country: country || null,
      zodiacSign: zodiacSign || null,
      languages: languages.length > 0 ? languages : null,
    });

    if (result.success) {
      setProfile(result.profile);
      onClose();
    } else {
      alert(result.error || 'Failed to save profile');
    }
    setSaving(false);
  };

  const handleBackdropClick = (e) => {
    if (e.target === modalRef.current) {
      onClose();
    }
  };

  const toggleLanguage = (langCode) => {
    if (languages.includes(langCode)) {
      setLanguages(languages.filter((l) => l !== langCode));
    } else if (languages.length < MAX_LANGUAGES) {
      setLanguages([...languages, langCode]);
    }
  };

  const removeLanguage = (langCode) => {
    setLanguages(languages.filter((l) => l !== langCode));
  };

  const filteredCountries = COUNTRIES.filter((c) =>
    c.name.toLowerCase().includes(countrySearch.toLowerCase())
  );

  const filteredLanguages = LANGUAGES.filter((l) =>
    l.name.toLowerCase().includes(languageSearch.toLowerCase())
  );

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleDeleteMemory = async () => {
    if (!memoryToDelete || !user) return;

    setDeleting(true);
    try {
      await deleteMemoryV2(memoryToDelete.id, user);
      // Remove from local state
      setMemories((prev) => prev.filter((m) => m.id !== memoryToDelete.id));
      setMemoryToDelete(null);
      // Adjust current page if needed
      const newTotalPages = Math.ceil((memories.length - 1) / notesPerPage);
      if (currentPage >= newTotalPages && newTotalPages > 0) {
        setCurrentPage(newTotalPages - 1);
      }
    } catch (err) {
      alert('Failed to delete memory. Please try again.');
    }
    setDeleting(false);
  };

  if (!isOpen) return null;

  const displayName = user?.displayName || user?.email?.split('@')[0] || 'User';
  const photoURL = user?.photoURL;
  const bioCharsRemaining = MAX_BIO - bio.length;

  // Use portal to render modal at document body level to escape transformed parent containers
  return createPortal(
    <div
      ref={modalRef}
      className="profile-modal-overlay"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="profile-modal-title"
    >
      <div className="profile-modal">
        {/* Header */}
        <div className="profile-modal-header">
          <h2 id="profile-modal-title">Your Profile</h2>
          <button className="close-button" onClick={onClose} aria-label="Close modal">
            x
          </button>
        </div>

        {loading ? (
          <div className="profile-loading">
            <div className="loading-spinner-large"></div>
            <p>Loading profile...</p>
          </div>
        ) : (
          <div className="profile-modal-content">
            {/* Profile Image Section */}
            <div className="profile-image-section">
              {photoURL ? (
                <img src={photoURL} alt={displayName} className="profile-avatar-large" />
              ) : (
                <div className="profile-avatar-large profile-avatar-placeholder">
                  {displayName.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="profile-name">{displayName}</div>
              <div className="profile-email">{user?.email}</div>
            </div>

            {/* Form Fields */}
            <div className="profile-form">
              {/* Handle */}
              <div className="form-group">
                <label htmlFor="handle">Handle</label>
                <div className="handle-input-wrapper">
                  <span className="handle-prefix">@</span>
                  <input
                    type="text"
                    id="handle"
                    value={handle}
                    onChange={handleHandleChange}
                    placeholder="your_handle"
                    maxLength={30}
                    className={`handle-input ${
                      handleStatus.checking
                        ? 'checking'
                        : handleStatus.valid && handleStatus.available
                        ? 'valid'
                        : handle.length >= 3
                        ? 'invalid'
                        : ''
                    }`}
                  />
                  {handleStatus.checking && <span className="handle-status checking">...</span>}
                  {!handleStatus.checking && handleStatus.valid && handleStatus.available && handle.length >= 3 && (
                    <span className="handle-status available">available</span>
                  )}
                  {!handleStatus.checking && !handleStatus.available && handle.length >= 3 && (
                    <span className="handle-status taken">taken</span>
                  )}
                </div>
                {handleStatus.error && <p className="form-error">{handleStatus.error}</p>}
                <p className="form-hint">3-30 characters: lowercase letters, numbers, underscores</p>
              </div>

              {/* Bio */}
              <div className="form-group">
                <label htmlFor="bio">Bio</label>
                <textarea
                  id="bio"
                  value={bio}
                  onChange={(e) => setBio(e.target.value.slice(0, MAX_BIO))}
                  placeholder="Tell us about yourself..."
                  rows={3}
                  maxLength={MAX_BIO}
                  className="bio-textarea"
                />
                <div className={`char-counter ${bioCharsRemaining < 30 ? 'warning' : ''}`}>
                  {bioCharsRemaining}
                </div>
              </div>

              {/* Country */}
              <div className="form-group">
                <label htmlFor="country">Country</label>
                <div className="searchable-dropdown">
                  {country && !countrySearch && (
                    <span className="input-flag">{COUNTRIES.find(c => c.name === country)?.flag}</span>
                  )}
                  <input
                    type="text"
                    id="country"
                    value={countrySearch || country}
                    onChange={(e) => {
                      setCountrySearch(e.target.value);
                      setShowCountryDropdown(true);
                    }}
                    onFocus={() => setShowCountryDropdown(true)}
                    placeholder="Select your country"
                    className={`dropdown-input ${country && !countrySearch ? 'has-flag' : ''}`}
                  />
                  {country && (
                    <button
                      type="button"
                      className="clear-btn"
                      onClick={() => {
                        setCountry('');
                        setCountrySearch('');
                      }}
                    >
                      x
                    </button>
                  )}
                  {showCountryDropdown && (
                    <>
                      <div className="dropdown-backdrop" onClick={() => setShowCountryDropdown(false)} />
                      <div className="dropdown-list">
                        {filteredCountries.map((c) => (
                          <button
                            key={c.code}
                            type="button"
                            className={`dropdown-item ${country === c.name ? 'selected' : ''}`}
                            onClick={() => {
                              setCountry(c.name);
                              setCountrySearch('');
                              setShowCountryDropdown(false);
                            }}
                          >
                            <span className="item-flag">{c.flag}</span> {c.name}
                          </button>
                        ))}
                        {filteredCountries.length === 0 && (
                          <div className="dropdown-empty">No countries found</div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Zodiac Sign */}
              <div className="form-group">
                <label htmlFor="zodiac">Zodiac Sign</label>
                <select
                  id="zodiac"
                  value={zodiacSign}
                  onChange={(e) => setZodiacSign(e.target.value)}
                  className="zodiac-select"
                >
                  <option value="">Select your sign</option>
                  {ZODIAC_SIGNS.map((sign) => (
                    <option key={sign.id} value={sign.id}>
                      {sign.emoji} {sign.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Languages */}
              <div className="form-group">
                <label>Languages (max {MAX_LANGUAGES})</label>
                <div className="languages-chips">
                  {languages.map((langCode) => {
                    const lang = LANGUAGES.find((l) => l.code === langCode);
                    return (
                      <span key={langCode} className="language-chip">
                        <span className="chip-flag">{lang?.flag}</span> {lang?.name || langCode}
                        <button type="button" onClick={() => removeLanguage(langCode)}>
                          x
                        </button>
                      </span>
                    );
                  })}
                  {languages.length < MAX_LANGUAGES && (
                    <button
                      type="button"
                      className="add-language-btn"
                      onClick={() => setShowLanguagesDropdown(!showLanguagesDropdown)}
                    >
                      + Add
                    </button>
                  )}
                </div>
                {showLanguagesDropdown && (
                  <>
                    <div className="dropdown-backdrop" onClick={() => setShowLanguagesDropdown(false)} />
                    <div className="languages-dropdown">
                      <input
                        type="text"
                        value={languageSearch}
                        onChange={(e) => setLanguageSearch(e.target.value)}
                        placeholder="Search languages..."
                        className="language-search"
                        autoFocus
                      />
                      <div className="dropdown-list">
                        {filteredLanguages.map((lang) => (
                          <button
                            key={lang.code}
                            type="button"
                            className={`dropdown-item ${languages.includes(lang.code) ? 'selected' : ''}`}
                            onClick={() => toggleLanguage(lang.code)}
                            disabled={languages.length >= MAX_LANGUAGES && !languages.includes(lang.code)}
                          >
                            <span className="item-flag">{lang.flag}</span> {lang.name}
                            {languages.includes(lang.code) && <span className="checkmark">check</span>}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Memory Slideshow */}
            {memories.length > 0 && (
              <div className="memory-slideshow">
                <h3>Your Memories <span className="memory-count">({memories.length})</span></h3>
                <div className="slideshow-container">
                  <div className="memory-notes-row">
                    {getCurrentPageMemories().map((memory, index) => (
                      <div
                        key={memory.id}
                        className="memory-note"
                        style={{
                          '--note-color': memory.color || '#E6B3C2',
                          '--note-rotation': `${-3 + index * 2}deg`,
                        }}
                      >
                        <button
                          type="button"
                          className="note-delete-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMemoryToDelete(memory);
                          }}
                          aria-label="Delete memory"
                        >
                          x
                        </button>
                        <p className="note-text">{memory.text}</p>
                        <span className="note-date">{formatDate(memory.created_at)}</span>
                      </div>
                    ))}
                  </div>
                  {/* Page indicators */}
                  {totalPages > 1 && (
                    <div className="slideshow-dots">
                      {Array.from({ length: Math.min(totalPages, 10) }).map((_, index) => (
                        <button
                          key={index}
                          className={`dot ${index === currentPage ? 'active' : ''}`}
                          onClick={() => setCurrentPage(index)}
                          aria-label={`Go to page ${index + 1}`}
                        />
                      ))}
                      {totalPages > 10 && (
                        <span className="dot-overflow">+{totalPages - 10}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="profile-actions">
              <button type="button" className="action-button secondary" onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className="action-button primary"
                onClick={handleSave}
                disabled={saving || !handleStatus.valid || !handleStatus.available}
              >
                {saving ? 'Saving...' : 'Save Profile'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {memoryToDelete && (
        <div className="delete-confirm-overlay" onClick={() => !deleting && setMemoryToDelete(null)}>
          <div className="delete-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Memory?</h3>
            <p>Are you sure you want to delete this memory? This action cannot be undone.</p>
            <div
              className="delete-preview-note"
              style={{ backgroundColor: memoryToDelete.color || '#E6B3C2' }}
            >
              <p>{memoryToDelete.text}</p>
            </div>
            <div className="delete-confirm-actions">
              <button
                type="button"
                className="action-button secondary"
                onClick={() => setMemoryToDelete(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="action-button danger"
                onClick={handleDeleteMemory}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
